import { commonUtils } from '@sato/components';
import { Asset } from 'contentful';
import { isEmpty } from 'lodash';

import {
  CONTENT_TYPE,
  CONTENTFUL_DEFAULT_LOCALE_CODE,
  ContentfulAsset,
  EntryType,
  GenericEntry,
  isEntryType,
  LOCALE_CODE,
  LocalizedEntry,
  LocalizedField,
  ParseAsset,
  ParsedEntry,
  ParsedLocalizedEntry,
  ParsedLocalizedFields,
  ParsedNestedLocalizedEntry,
  ParsedNestedLocalizedFields,
} from '../types';

const defaultSatoLocale = {
  language: 'fi' satisfies CONTENTFUL_DEFAULT_LOCALE_CODE,
};

const isAsset = (entry: any): entry is Asset => {
  return entry?.fields?.file !== undefined;
};

export const parseEntry = <T extends EntryType>(
  contentfulEntry: T
): ParsedEntry<T> | null => {
  const { fields, sys } = contentfulEntry;

  if (!fields || isEmpty(fields)) return null;

  // fields might be undefined in some very odd corner cases
  return Object.entries(fields ?? {}).reduce(
    (acc, [currentKey, currentValue]) => {
      // Here we handle the sub entries recursively and strip the contentful wrapping
      if (isAsset(currentValue)) {
        return {
          ...acc,
          [currentKey]: {
            ...parseAsset(currentValue.fields),
          },
        };
      }
      // is array of assets
      else if (Array.isArray(currentValue) && currentValue.every(isAsset)) {
        return {
          ...acc,
          [currentKey]: currentValue.map((item) => ({
            ...parseAsset(item.fields),
          })),
        };
      }
      // is nested entry
      else if (isEntryType(currentValue)) {
        return {
          ...acc,
          [currentKey]: {
            ...parseEntry(currentValue),
          },
        };
      }
      // is array of entries
      else if (Array.isArray(currentValue) && currentValue.every(isEntryType)) {
        return {
          ...acc,
          [currentKey]: currentValue.map((item) => ({
            ...parseEntry(item),
          })),
        };
      }

      return {
        ...acc,
        [currentKey]: currentValue,
      };
    },
    {
      // We inject this id here to the data so its usable if the sub entry has its own logic, like in benefits every benefit has own page that needs the id.
      id: sys.id,
      contentType: sys.contentType.sys.id,
    } as ParsedEntry<T>
  );
};

export const isLocalizedField = (
  object: unknown
): object is LocalizedField<unknown> =>
  commonUtils.nonNullable(object) &&
  typeof object === 'object' &&
  defaultSatoLocale.language in object;

const getContentType = <T extends EntryType | ContentfulAsset>(
  entry: LocalizedEntry<T>
) =>
  'contentType' in entry.sys
    ? (entry.sys.contentType.sys.id as CONTENT_TYPE)
    : 'asset';

export const isLocalizedContentfulObject = (
  object: unknown
): object is LocalizedEntry<GenericEntry | ContentfulAsset> => {
  if (!(commonUtils.nonNullable(object) && typeof object === 'object'))
    return false;
  if (!('sys' in object && 'fields' in object)) return false;

  // workaround to keep TS happy
  const { fields } = object as Record<string, unknown>;
  return (
    commonUtils.nonNullable(fields) &&
    typeof fields === 'object' &&
    Object.values(fields).some(isLocalizedField)
  );
};

type ParseOptions<T extends EntryType | ContentfulAsset> = Partial<{
  requiredFields?: Array<keyof T['fields']>;
}>;

export const parseLocalizedFields = <T extends EntryType | ContentfulAsset>(
  fields: LocalizedEntry<T>['fields'],
  language: LOCALE_CODE | undefined,
  options: ParseOptions<T> = {}
): ParsedLocalizedFields<T> =>
  Object.entries(fields).reduce(
    (acc, [key, value]) => ({
      ...acc,
      [key]:
        (language && value[language]) ??
        // if the field is required, don't fallback to the default locale
        (options.requiredFields?.includes(key as keyof T['fields'])
          ? undefined
          : value[defaultSatoLocale.language]),
    }),
    {} as ParsedLocalizedFields<T>
  );

// Parse all nested child entries in a localized entry object
// TODO: Could this be using parseEntry?
export const parseNestedLocalizedFields = <
  T extends EntryType | ContentfulAsset,
>(
  fields: LocalizedEntry<T>['fields'],
  language: LOCALE_CODE | undefined,
  options: ParseOptions<T> = {}
): ParsedNestedLocalizedFields<T> =>
  Object.entries(fields).reduce((acc, [key, value]) => {
    const fieldValue =
      (language && value[language]) ??
      // if the field is required, don't fallback to the default locale
      (options.requiredFields?.includes(key as keyof T['fields'])
        ? undefined
        : value[defaultSatoLocale.language]);
    if (Array.isArray(fieldValue)) {
      return {
        ...acc,
        [key]: isLocalizedContentfulObject(fieldValue[0])
          ? fieldValue.map((value) =>
              parseNestedLocalizedEntry(value, language)
            )
          : fieldValue,
      };
    }

    return {
      ...acc,
      [key]: isLocalizedContentfulObject(fieldValue)
        ? parseNestedLocalizedEntry(fieldValue, language)
        : fieldValue,
    };
  }, {} as ParsedNestedLocalizedFields<T>);

// TODO: Could this be using parseEntry?
export const parseLocalizedEntry = <T extends EntryType | ContentfulAsset>(
  entry: LocalizedEntry<T>,
  language: LOCALE_CODE | undefined,
  options: ParseOptions<T> = {}
): ParsedLocalizedEntry<T> => ({
  ...parseLocalizedFields<T>(entry.fields, language, options),
  type: getContentType(entry),
  id: entry.sys.id,
});

export const parseNestedLocalizedEntry = <
  T extends EntryType | ContentfulAsset | GenericEntry,
>(
  entry: LocalizedEntry<T>,
  language: LOCALE_CODE | undefined,
  options: ParseOptions<T> = {}
): ParsedNestedLocalizedEntry<T> | undefined => ({
  ...parseNestedLocalizedFields<T>(entry.fields, language, options),
  type: getContentType(entry),
  id: entry.sys.id,
});

export const parseAsset = (asset: ContentfulAsset['fields']): ParseAsset => {
  const rawSrc = asset.file.url ?? asset.file.upload;

  return {
    title: asset.title,
    // add protocol to the start of URL if it's missing
    src: new RegExp('^//.*$').test(rawSrc) ? `https:${rawSrc}` : rawSrc,
    srcType: asset.file.contentType.split('/')[0] as 'image' | 'video',
    alt: asset.description,
    source: 'contentful',
  };
};
